import {Brand, BrandSimple} from '../../models/brand.model';
import {Injectable} from '@angular/core';
import {NavService} from '../../services/nav.service';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {BrandsService} from '../../services/brands.service';
import {clearFormValidationErrors, setFormStatus, setFormValidationErrors} from '../../helpers/form.helper';
import {catchError, mergeMap, tap} from 'rxjs/operators';
import {SearchList} from '../../models/search-list.model';
import {FormStatus} from '../../enums/file-status.enum';
import {handleFetchListPage} from '../../helpers/search.helper';
import {FormState} from '../../models/form-state.model';
import {BrandsActions} from './brands.actions';


interface BrandsStateModel {
    form: FormState;
    list: SearchList<Brand>;
    fieldList: string[];
    createModalIsOpen: boolean;
    filteredBrandFieldList: Brand[];
    modelSearchList: BrandSimple[];
    brandSearchList: BrandSimple[];
}

const STATE_NAME = 'brands';

@State<BrandsStateModel>({
    name: STATE_NAME,
    defaults: {
        form: new FormState(),
        list: new SearchList<Brand>({filters: new FormState()}),
        fieldList: null,
        filteredBrandFieldList: null,
        createModalIsOpen: false,
        modelSearchList: null,
        brandSearchList: null
    }
})

@Injectable()
export class BrandsState {
    constructor(
        private navService: NavService,
        private brandsService: BrandsService
    ) {
    }

    @Selector()
    static formErrors(state) {
        return state.form.validationErrors;
    }

    @Selector()
    static brandsCreateModalIsOpen(state) {
        return state.createModalIsOpen;
    }

    @Selector()
    static brandsList(state) {
        return state.list;
    }

    @Selector()
    static brandsFormErrors(state) {
        return state.form.validationErrors;
    }

    @Selector()
    static brandsFieldList(state) {
        return state.fieldList;
    }

    @Selector()
    static brandsFieldListCodes(state) {
        return state.fieldList.map(item => item.code);
    }

    @Selector()
    static brandSearchList(state) {
        return state.brandSearchList.map(item => item.code);
    }

    @Selector()
    static modelSearchList(state) {
        return state.modelSearchList.map(item => item.code);
    }

    @Selector()
    static filteredBrandsFieldListCodes(state) {
        return state.filteredBrandFieldList.map(item => item.brandCode).sort();
    }

    @Action(BrandsActions.GetListPage)
    getListPage(ctx: StateContext<BrandsStateModel>, action: BrandsActions.GetListPage) {
        return handleFetchListPage(ctx, action.params, (props) => this.brandsService.fetchList(props));
    }

    @Action(BrandsActions.Add)
    addBrand(ctx: StateContext<BrandsStateModel>, {brand}: BrandsActions.Add) {
        setFormStatus(ctx, FormStatus.PENDING, STATE_NAME);
        return this.brandsService.add(brand).pipe(
            mergeMap(() => {
                clearFormValidationErrors(ctx);
                return ctx.dispatch([
                    new BrandsActions.SetModalVisibility(false),
                    new BrandsActions.GetListPage()]);
            }),
            catchError(({error}) => ctx.dispatch(new BrandsActions.FormFailure(error)))
        );
    }

    @Action(BrandsActions.Delete)
    deleteBrand(ctx: StateContext<BrandsStateModel>, {brand}: BrandsActions.Delete) {
        return this.brandsService.delete(brand).subscribe(
            () => ctx.dispatch(new BrandsActions.GetListPage())
        );
    }

    @Action(BrandsActions.FormFailure)
    addDocumentFailure(ctx: StateContext<BrandsStateModel>, {validationErrors = []}: BrandsActions.FormFailure) {
        setFormValidationErrors(ctx, validationErrors, STATE_NAME);
    }

    @Action(BrandsActions.FieldList)
    getFieldList(ctx: StateContext<BrandsStateModel>, {brand}: BrandsActions.FieldList) {
        return this.brandsService.getFieldList(brand).pipe(
            tap((items: BrandSimple[]) => {
                ctx.patchState({
                    fieldList: items.map(item => item.code)
                });
            })
        );
    }

    @Action(BrandsActions.SetModalVisibility)
    setModalVisibility(ctx: StateContext<BrandsStateModel>, {isVisible}: BrandsActions.SetModalVisibility) {
        if (!isVisible) {
            clearFormValidationErrors(ctx);
        }
        ctx.patchState({
            createModalIsOpen: isVisible,
        });
    }

    @Action(BrandsActions.FilteredBrandList)
    getFilteredBrandList(ctx: StateContext<BrandsStateModel>, {brand}: BrandsActions.FilteredBrandList) {
        return this.brandsService.getFilteredBrandsList(brand).pipe(
            tap((items: Brand[]) => {
                ctx.patchState({
                    filteredBrandFieldList: items
                });
            })
        );
    }

    @Action(BrandsActions.BrandsSearchList)
    brandsSearchList(ctx: StateContext<BrandsStateModel>, {searchData}: BrandsActions.BrandsSearchList) {
        return this.brandsService.brandsSearchList(searchData.codeSearch).subscribe(
            (brandSearchList) => {
                ctx.patchState({brandSearchList});

            });
    }

    @Action(BrandsActions.ModelSearchList)
    modelSearchList(ctx: StateContext<BrandsStateModel>, {searchData}: BrandsActions.ModelSearchList) {
        return this.brandsService.modelSearchList(searchData.codeSearch, searchData.brand).subscribe(
            (modelSearchList) => {
                ctx.patchState({modelSearchList});
            });
    }

    @Action(BrandsActions.ModelListClear)
    modelListClear(ctx: StateContext<BrandsStateModel>) {
        ctx.patchState({modelSearchList: null});
    }
}
